import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket, faXmark } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue, formatRFCDate, dateToRFCDateValue, toCurrencyCentValue } from '@/utils/helpers';
import type { JournalStockPosition, JournalAssignmentDecision } from '@/models';
import {
  pnlColorClass,
  ASSIGNMENT_DECISION_LABELS,
  ASSIGNMENT_DECISION_OPTIONS,
  JOURNAL_FIELD_CLASS,
  JOURNAL_LABEL_CLASS,
} from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';
import { showSuccessToast, showErrorToast } from '@/utils/toasts';

export default function JournalAssignmentWorkflow() {
  const { data, loading, error, reload } = useJournalResource(
    () => APIRoutes.listJournalStockPositions({ assignedOnly: true, status: 'OPEN', limit: 200 }),
    [],
    'Fehler beim Laden der Zuteilungen',
  );
  const positions = data?.stockPositions ?? [];
  const [busy, setBusy] = useState(false);

  const [closeTarget, setCloseTarget] = useState<JournalStockPosition | null>(null);
  const [closePrice, setClosePrice] = useState('');
  // The price starts empty, so its "required" message stays hidden until the user has typed once.
  const [closePriceTouched, setClosePriceTouched] = useState(false);
  const [closeDate, setCloseDate] = useState(dateToRFCDateValue(new Date()));

  const handleDecisionChange = async (pos: JournalStockPosition, decision: JournalAssignmentDecision) => {
    setBusy(true);
    try {
      await APIRoutes.updateJournalStockPosition(pos.id, { assignmentDecision: decision });
      showSuccessToast('Entscheidung gespeichert');
      reload();
    } catch (err) {
      console.error('Failed to update decision:', err);
      showErrorToast('Entscheidung konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  };

  const closePriceNum = closePrice.trim() === '' ? Number.NaN : Number(closePrice);
  const closePriceError = closePrice.trim() === ''
    ? 'Bitte einen Verkaufskurs angeben.'
    : Number.isNaN(closePriceNum)
      ? 'Bitte eine gültige Zahl eingeben.'
      : closePriceNum < 0
        ? 'Der Verkaufskurs darf nicht negativ sein.'
        : null;
  const closeDateError = closeDate === '' ? 'Bitte ein Datum angeben.' : null;
  const closeValid = closePriceError === null && closeDateError === null;

  const submitClose = async () => {
    if (closeTarget === null || !closeValid) return;
    setBusy(true);
    try {
      await APIRoutes.updateJournalStockPosition(closeTarget.id, {
        status: 'CLOSED',
        assignmentDecision: 'CLOSED',
        closeDate,
        closePrice: toCurrencyCentValue(closePriceNum),
      });
      setCloseTarget(null);
      setClosePrice('');
      showSuccessToast('Position geschlossen');
      reload();
    } catch (err) {
      console.error('Failed to close position:', err);
      showErrorToast('Position konnte nicht geschlossen werden');
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<JournalStockPosition>[] = [
    {
      key: 'symbol',
      header: 'Symbol',
      primary: true,
      render: (p) => <span className="font-medium text-gray-900 dark:text-white">{p.symbolTicker}</span>,
    },
    {
      key: 'quantity',
      header: 'Stück',
      align: 'right',
      render: (p) => p.quantity,
    },
    {
      key: 'costBasis',
      header: 'Einstand',
      align: 'right',
      render: (p) => formatCurrencyCentValue(p.costBasis, p.currency),
    },
    {
      key: 'currentPrice',
      header: 'Akt. Kurs',
      align: 'right',
      render: (p) => (p.currentPrice === null ? '—' : formatCurrencyCentValue(p.currentPrice, p.currency)),
    },
    {
      key: 'unrealizedPnL',
      header: 'Unrealisiert',
      align: 'right',
      render: (p) => (
        <span className={`font-medium ${pnlColorClass(p.unrealizedPnL)}`}>
          {p.unrealizedPnL === null ? '—' : formatCurrencyCentValue(p.unrealizedPnL, p.currency)}
        </span>
      ),
    },
    {
      key: 'openDate',
      header: 'Zugeteilt',
      render: (p) => <span className="text-gray-500 dark:text-gray-400">{formatRFCDate(p.openDate)}</span>,
    },
    {
      key: 'decision',
      header: 'Entscheidung',
      render: (p) => (
        <select
          className={JOURNAL_FIELD_CLASS}
          aria-label={`Entscheidung für ${p.symbolTicker}`}
          value={p.assignmentDecision}
          disabled={busy}
          onChange={(e) => handleDecisionChange(p, e.target.value as JournalAssignmentDecision)}
        >
          {ASSIGNMENT_DECISION_OPTIONS.filter((d) => d !== 'CLOSED').map((d) => (
            <option key={d} value={d}>{ASSIGNMENT_DECISION_LABELS[d]}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'actions',
      header: 'Aktionen',
      align: 'right',
      actions: true,
      render: (p) => (
        <Button
          variant="secondary"
          size="sm"
          title="Verkaufen / Schließen"
          disabled={busy}
          onClick={() => { setCloseTarget(p); setClosePrice(''); setClosePriceTouched(false); setCloseDate(dateToRFCDateValue(new Date())); }}
        >
          Schließen
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Aus Put-Zuteilungen entstandene Aktienpositionen: entscheide, ob du hältst, Covered Calls schreibst oder verkaufst.
      </p>

      {error !== null ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <DataTable<JournalStockPosition>
          columns={columns}
          rows={positions}
          keyExtractor={(p) => p.id}
          loading={loading}
          emptyState={(
            <EmptyState
              icon={faArrowRightFromBracket}
              title="Keine offenen Zuteilungen"
              description="Markiere einen Put-Trade als „zugeteilt“, damit die daraus entstandene Aktienposition hier auftaucht."
            />
          )}
        />
      )}

      <Modal isOpen={closeTarget !== null} onClose={() => setCloseTarget(null)} title="Position schließen" maxWidth="md" closeDisabled={busy} icon={<FontAwesomeIcon icon={faXmark} className="w-5 h-5 text-blue-600 dark:text-blue-400" />}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {closeTarget !== null && `${closeTarget.symbolTicker} (${closeTarget.quantity} Stück) verkaufen / als geschlossen markieren.`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="position-close-price">Verkaufskurs / Aktie ($)</label>
              <input
                id="position-close-price"
                className={`w-full ${JOURNAL_FIELD_CLASS} ${!closePriceTouched || closePriceError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="number"
                step="0.01"
                value={closePrice}
                onChange={(e) => { setClosePrice(e.target.value); setClosePriceTouched(true); }}
                onBlur={() => setClosePriceTouched(true)}
                placeholder="155.00"
                aria-invalid={closePriceError !== null}
              />
              {closePriceTouched && closePriceError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closePriceError}</p>}
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="position-close-date">Datum</label>
              <input
                id="position-close-date"
                className={`w-full ${JOURNAL_FIELD_CLASS} ${closeDateError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                aria-invalid={closeDateError !== null}
              />
              {closeDateError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closeDateError}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCloseTarget(null)} disabled={busy}>Abbrechen</Button>
            <Button variant="primary" onClick={submitClose} loading={busy} disabled={!closeValid}>Schließen</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
